import React, { useCallback, useEffect } from 'react';
import c from 'classnames';
import {
  motion,
  AnimatePresence,
  MotionConfig,
  type HTMLMotionProps,
} from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { FormRadioButtons, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { useQuizContext } from '../../../context/QuizContext';
import WebformAssessment from '../Assessment';
import WebformQuiz from '../Quiz';
import type { WebformQuizRadios as Type } from '../types';

import './style.scss';

// Type assertion needed for framer-motion compatibility with newer React types.
const MotionDiv = motion.div as unknown as React.FC<HTMLMotionProps<'div'>>;

export interface WebformQuizCheckboxesProps {
  entity: ImmutableEntity<Type>;
}

function validateAnswer(givenAnswer: string, correctAnswers: string[]) {
  return correctAnswers.includes(givenAnswer) ? 'correct' : 'incorrect';
}

const WebformQuizRadios: React.FC<WebformQuizCheckboxesProps> = (props) => {
  const {
    entity,
  } = props;

  const { t } = useTranslation();
  const { currentQuizQuestion, updateQuizQuestion, updateCurrentQuizQuestionId } = useQuizContext();

  const component = entity.toJS();

  // Base props.
  const key = component['#webform_key'];
  const title = component['#title'];
  const required = component['#required'] || false;
  const requiredError = component['#required_error'] || '';
  const layoutOptions = component['#options_display'] || '';
  const elementClassNames = component['#attributes']?.class[0] || '';
  const wrapperClassNames = c('WebformQuizRadios', component['#wrapper_attributes']?.class[0], {
    [`WebformQuizRadios--${String(layoutOptions)}`]: layoutOptions,
  });

  // Options props.
  const options = component['#options'];

  // Quiz props.
  const correctAnswers = component['#correct_answers'].split(',').map((item) => item.trim());
  const description = component['#description_question'];
  const descriptionCorrectAnswer = component['#description_correct_answer'];
  const descriptionIncorrectAnswer = component['#description_incorrect_answer'];
  const showAnswerOnNewPage = component['#show_answer_on_new_page'] || false;

  const validation = useCallback((value: string) => {
    let error;
    if (required && value.length === 0) {
      error = requiredError || t(['webform.components.quiz_radios.required', 'webform.required', 'webform_required']);
    }
    return error;
  }, [required, requiredError, t]);

  useEffect(() => {
    updateCurrentQuizQuestionId(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={wrapperClassNames}>
      <MotionConfig transition={{ duration: 0.3 }}>
        <AnimatePresence initial={false} mode="wait">
          {currentQuizQuestion?.currentPage === 'question' ? (
            <MotionDiv
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="WebformQuizRadios__header">
                <WebformAssessment.Count />
              </div>

              <FormRadioButtons
                className={elementClassNames}
                fieldName={key}
                required={required}
                title={title}
                options={options}
                validation={validation}
                description={description}
                onChange={(givenAnswer: string) => {
                  updateQuizQuestion({
                    id: key,
                    givenAnswers: [givenAnswer],
                    isCorrect: validateAnswer(givenAnswer, correctAnswers) === 'correct',
                    // If the user has answered the question and 'showAnswerOnNewPage' is false,
                    // we want to show the answer immediately, so set current page to 'answer'.
                    currentPage: givenAnswer && !showAnswerOnNewPage ? 'answer' : 'question',
                  });
                }}
              />
            </MotionDiv>
          ) : (
            <MotionDiv
              key="description"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <WebformQuiz.Thumb
                className="WebformQuizRadios__thumb"
                direction={currentQuizQuestion?.isCorrect ? 'up' : 'down'}
              />
              <div className="WebformQuizRadios__description">
                <WysiwygField
                  property="schema:text"
                  html={currentQuizQuestion?.isCorrect ?
                    descriptionCorrectAnswer :
                    descriptionIncorrectAnswer}
                />
              </div>
            </MotionDiv>
          )}
        </AnimatePresence>
      </MotionConfig>
    </div>
  );
};

export default WebformQuizRadios;
